import * as z from "zod";

// The images of a product, a style-colour (B-56, ADR-213): uploaded first in the folder `product-images` of the files
// worker (ADR-069), then linked in order; the first one is the main one.

/** The folder of the files worker the images are uploaded into (migration 0108): JPEG and PNG. */
export const PRODUCT_IMAGES_FOLDER = "product-images";

/** How many images a product holds at most. */
export const MAX_PRODUCT_IMAGES = 20;

const productImageIds = z.array(z.uuid()).min(1).max(MAX_PRODUCT_IMAGES);

export const listProductImagesInput = z.object({ productId: z.uuid() });
export type ListProductImagesInput = z.input<typeof listProductImagesInput>;

/**
 * Files already uploaded in `product-images`, added after the images the product has, in the order given, all or
 * none. A file already among them is left where it is, so a retry adds nothing twice.
 */
export const addProductImagesInput = z.object({ productId: z.uuid(), fileIds: productImageIds });
export type AddProductImagesInput = z.input<typeof addProductImagesInput>;
export const addProductImagesBody = addProductImagesInput.omit({ productId: true });

/** Every image of the product, in the new order: the first one becomes the main one. */
export const reorderProductImagesInput = z.object({ productId: z.uuid(), imageIds: productImageIds });
export type ReorderProductImagesInput = z.input<typeof reorderProductImagesInput>;
export const reorderProductImagesBody = reorderProductImagesInput.omit({ productId: true });

/** Takes an image off the product; the file stays in its folder. */
export const removeProductImageInput = z.object({ productId: z.uuid(), imageId: z.uuid() });
export type RemoveProductImageInput = z.input<typeof removeProductImageInput>;

/** The bytes of an image of the product. */
export const downloadProductImageInput = z.object({ productId: z.uuid(), imageId: z.uuid() });
export type DownloadProductImageInput = z.input<typeof downloadProductImageInput>;

/** An image of a product: its bytes come from the catalog, never from a URL to the bucket. */
export const productImageDto = z
  .object({
    imageId: z.uuid(),
    fileId: z.uuid(),
    /** From 1: the first is the main image. */
    position: z.number().int().min(1),
    filename: z.string(),
    mimeType: z.string().nullable(),
    sizeBytes: z.number().int().nullable(),
    uploadedAt: z.iso.datetime(),
  })
  .meta({ id: "ProductImage" });

/** The images of a product, in their order. */
export const productImagesDto = z.object({ productId: z.uuid(), images: z.array(productImageDto) });
